import React, { useContext, useEffect, useMemo, useState } from "react";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import MenuButton from "../MenuButton/MenuButton";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./DailyReport.css";

const money = (n, blankZero = false) => {
  const v = Number(n || 0);
  if (blankZero && v === 0) return "";
  return v.toLocaleString("en-PK", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

export default function DailyReport() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;
  const today = new Date().toLocaleDateString("en-CA");

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(branch_id || "");
  const [date, setDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await UserServices.branchName();
        const list = response.data?.data || [];
        setBranches(Array.isArray(list) ? list : []);
        if (branch_id) {
          const match = list.find((b) => String(b.id) === String(branch_id));
          if (match) setSelectedBranch(String(match.id));
        }
      } catch (err) {
        setBranches([]);
      }
    };
    fetchBranches();
  }, [branch_id]);

  const loadReport = async () => {
    if (!selectedBranch || !date) {
      setError("Please select branch and date.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await UserServices.getDailyReport(selectedBranch, date);
      setReport(res.data?.data || null);
      if (!res.data?.data) {
        setError("No data for this day.");
      }
    } catch (err) {
      setReport(null);
      setError(err.response?.data?.message || "Failed to load daily report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBranch && date) {
      loadReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  const sales = report?.sales_summary || {};
  const cash = report?.cash_reconciliation || {};
  const balances = report?.balances || {};
  const expenses = report?.expenses || [];
  const packages = report?.package_breakdown || [];

  const branchName = useMemo(() => {
    const b = branches.find(
      (x) => String(x.id) === String(selectedBranch) || String(x.branch_id) === String(selectedBranch)
    );
    return b?.name || b?.branch_name || `Branch ${selectedBranch}`;
  }, [branches, selectedBranch]);

  const downloadPdf = () => {
    if (!report) return;
    const doc = new jsPDF("l", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    let y = 12;

    doc.setFillColor(139, 0, 0);
    doc.rect(10, 8, pageW - 20, 10, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.text(`Daily Report — ${report.date_label || date}  |  ${branchName}`, pageW / 2, 15, {
      align: "center",
    });
    doc.setTextColor(0, 0, 0);
    y = 24;

    const salesRows = [
      ["Total Sale", money(sales.total_sale)],
      ["Gym CC", money(sales.gym_cc)],
      ["Online", money(sales.online)],
      ["Cheque Transfer Gym", money(sales.cheque_transfer_gym)],
      ["Vitality Studio Cash", money(sales.vitality_studio_cash)],
      ["Online Transfer Studio", money(sales.online_transfer_studio)],
      ["Studio CC", money(sales.studio_cc)],
      ["Balance Done Today", money(sales.balance_done_today)],
      ["Total Cash", money(sales.total_cash)],
    ];

    doc.autoTable({
      startY: y,
      head: [["Sales Summary", "Amount"]],
      body: salesRows,
      theme: "grid",
      headStyles: { fillColor: [180, 0, 30], textColor: 255, fontSize: 8 },
      styles: { fontSize: 7, cellPadding: 1.5 },
      margin: { left: 10 },
      tableWidth: 70,
    });

    const leftBottom = doc.lastAutoTable.finalY + 4;

    doc.autoTable({
      startY: leftBottom,
      head: [["Balances", "Amount"]],
      body: [
        ["Balance Done Today", money(balances.balance_done_today)],
        ["Balance Recieved Today", money(balances.balance_received_today)],
        ["Total Monthly Balance Done", money(balances.total_monthly_balance_done)],
        ["Total Monthly Balance Recieved", money(balances.total_monthly_balance_received)],
        ["Total Monthly Balance Pending", money(balances.total_monthly_balance_pending)],
        ["Online Transfer Live, Virtual", money(balances.online_transfer_live_virtual)],
      ],
      theme: "grid",
      headStyles: { fillColor: [46, 125, 50], textColor: 255, fontSize: 8 },
      styles: { fontSize: 7, cellPadding: 1.5 },
      margin: { left: 10 },
      tableWidth: 70,
    });

    const expenseBody =
      expenses.length > 0
        ? expenses.map((e) => [e.description, money(e.amount)])
        : [["—", "0"]];
    expenseBody.push(["Total Expenses", money(report.total_expenses)]);

    doc.autoTable({
      startY: y,
      head: [["EXPENSES", "Amount"]],
      body: expenseBody,
      theme: "grid",
      headStyles: { fillColor: [180, 0, 30], textColor: 255, fontSize: 8 },
      styles: { fontSize: 7, cellPadding: 1.5 },
      margin: { left: 85 },
      tableWidth: 60,
    });

    const midY = doc.lastAutoTable.finalY + 4;
    doc.autoTable({
      startY: midY,
      head: [["Cash", "Amount"]],
      body: [
        ["TOTAL CASH", money(cash.total_cash_net)],
        ["Petty Cash B/F", money(cash.petty_cash_bf)],
        ["Cash Submit", money(cash.cash_submit)],
        ["Cash C/F", money(cash.cash_cf)],
      ],
      theme: "grid",
      headStyles: { fillColor: [180, 0, 30], textColor: 255, fontSize: 8 },
      styles: { fontSize: 7, cellPadding: 1.5 },
      margin: { left: 85 },
      tableWidth: 60,
    });

    doc.autoTable({
      startY: y,
      head: [["Category", "Amount", "Qty"]],
      body: packages.map((p) => [p.label, money(p.amount), p.quantity || ""]),
      theme: "grid",
      headStyles: { fillColor: [123, 31, 162], textColor: 255, fontSize: 8 },
      styles: { fontSize: 6.5, cellPadding: 1.2 },
      margin: { left: 150 },
      tableWidth: pageW - 160,
      columnStyles: {
        0: { cellWidth: 55 },
        1: { halign: "right" },
        2: { halign: "center", cellWidth: 14 },
      },
    });

    doc.save(`daily-report-${date}.pdf`);
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="daily-report-page">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h4 className="mb-0">Daily Report</h4>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={loadReport}
                disabled={loading}
              >
                {loading ? "Loading…" : "Generate"}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={downloadPdf}
                disabled={!report || loading}
              >
                Download PDF
              </button>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body py-2">
              <div className="row g-2 align-items-end">
                <div className="col-md-4">
                  <label className="form-label small mb-1">Branch</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedBranch}
                    onChange={(e) => setSelectedBranch(e.target.value)}
                  >
                    <option value="">Select branch</option>
                    {branches.map((b) => (
                      <option key={b.id || b.branch_id} value={b.id || b.branch_id}>
                        {b.name || b.branch_name}
                      </option>
                    ))}
                    {!branches.length && selectedBranch ? (
                      <option value={selectedBranch}>Current branch</option>
                    ) : null}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small mb-1">Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary mt-3 mt-md-0"
                    onClick={loadReport}
                    disabled={loading}
                  >
                    Show report
                  </button>
                </div>
              </div>
            </div>
          </div>

          {error ? <div className="alert alert-warning py-2">{error}</div> : null}

          {report ? (
            <>
              <div className="dr-date-banner">{report.date_label}</div>

              <div className="dr-grid">
                <div className="dr-col-left">
                  <table className="dr-table dr-sales">
                    <tbody>
                      <tr className="dr-total">
                        <td>Total Sale</td>
                        <td>{money(sales.total_sale)}</td>
                      </tr>
                      <tr>
                        <td>Gym CC</td>
                        <td>{money(sales.gym_cc)}</td>
                      </tr>
                      <tr>
                        <td>Online</td>
                        <td>{money(sales.online)}</td>
                      </tr>
                      <tr>
                        <td>Cheque Transfer Gym</td>
                        <td>{money(sales.cheque_transfer_gym, true)}</td>
                      </tr>
                      <tr>
                        <td>Vitality Studio Cash</td>
                        <td>{money(sales.vitality_studio_cash, true)}</td>
                      </tr>
                      <tr>
                        <td>Online Transfer Studio</td>
                        <td>{money(sales.online_transfer_studio, true)}</td>
                      </tr>
                      <tr>
                        <td>Studio CC</td>
                        <td>{money(sales.studio_cc, true)}</td>
                      </tr>
                      <tr>
                        <td>Balance Done Today</td>
                        <td>{money(sales.balance_done_today, true)}</td>
                      </tr>
                      <tr className="dr-total">
                        <td>Total Cash</td>
                        <td>{money(sales.total_cash)}</td>
                      </tr>
                    </tbody>
                  </table>

                  <table className="dr-table dr-balances mt-3">
                    <tbody>
                      <tr>
                        <td>Balance Done Today</td>
                        <td>{money(balances.balance_done_today, true)}</td>
                      </tr>
                      <tr>
                        <td>Balance Recieved Today</td>
                        <td>{money(balances.balance_received_today)}</td>
                      </tr>
                      <tr>
                        <td>Total Monthly Balance Done</td>
                        <td>{money(balances.total_monthly_balance_done)}</td>
                      </tr>
                      <tr>
                        <td>Total Monthly Balance Recieved</td>
                        <td>{money(balances.total_monthly_balance_received)}</td>
                      </tr>
                      <tr>
                        <td>Total Monthly Balance Pending</td>
                        <td>{money(balances.total_monthly_balance_pending)}</td>
                      </tr>
                      <tr>
                        <td>Online Transfer Live, Virtual</td>
                        <td>{money(balances.online_transfer_live_virtual)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="dr-col-mid">
                  <table className="dr-table dr-expenses">
                    <thead>
                      <tr>
                        <th colSpan="2">EXPENSES</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.length === 0 ? (
                        <tr>
                          <td colSpan="2" className="text-muted">
                            No expenses
                          </td>
                        </tr>
                      ) : (
                        expenses.map((e) => (
                          <tr key={e.id}>
                            <td>{e.description}</td>
                            <td>{money(e.amount)}</td>
                          </tr>
                        ))
                      )}
                      <tr className="dr-total">
                        <td>Total Expenses</td>
                        <td>{money(report.total_expenses)}</td>
                      </tr>
                    </tbody>
                  </table>

                  <table className="dr-table dr-cash mt-3">
                    <tbody>
                      <tr className="dr-total">
                        <td>TOTAL CASH</td>
                        <td>{money(cash.total_cash_net)}</td>
                      </tr>
                      <tr className="dr-peach">
                        <td>Petty Cash B/F</td>
                        <td>{money(cash.petty_cash_bf)}</td>
                      </tr>
                      <tr className="dr-submit">
                        <td>Cash Submit</td>
                        <td>{money(cash.cash_submit)}</td>
                      </tr>
                      <tr className="dr-peach">
                        <td>Cash C/F</td>
                        <td>{money(cash.cash_cf)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="dr-col-right">
                  <table className="dr-table dr-packages">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Amount</th>
                        <th>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {packages.map((p) => (
                        <tr key={p.label}>
                          <td>{p.label}</td>
                          <td>{p.amount ? money(p.amount) : ""}</td>
                          <td>{p.quantity ? p.quantity : ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            !loading && (
              <div className="text-muted small">Select branch and date, then click Show report.</div>
            )
          )}
          </div>
        </div>
      </main>
    </div>
  );
}
